import { passwordReset } from "domains/account";
import { expand, generateKeys, sha512, useE2EEContext } from "lib/e2ee";
import { useLoggedinUser } from "lib/hooks";
import { useAppDispatch } from "lib/redux";
import { useState, FC } from "react";
import * as openpgp from 'openpgp';
import { InputGroup, Input, FormControl, InputLeftElement, InputRightElement, Tooltip, Button, Icon, Box } from '@chakra-ui/react';
import { Formik, Field, FieldProps, Form } from 'formik';
import { FormAlert, ButtonSubmit } from 'components';
import { object, SchemaOf } from 'yup';
import { validatePassword } from 'lib/utils/inputValidation';
import { PadlockIcon } from 'lib/icons/padlock';
import { AiOutlineEye } from '@react-icons/all-files/ai/AiOutlineEye';
import { AiOutlineEyeInvisible } from '@react-icons/all-files/ai/AiOutlineEyeInvisible';

type Props = {
  onComplete?: (user: any) => void
}

export const validation: SchemaOf<{ password: string }> = object().shape({
  password: validatePassword,
});

export const CreatePassword: FC<Props> = ( { onComplete } ) => {
  const [state, setState] = useState<any>({ error: null, success: null, showPassword: false });
  const dispatch = useAppDispatch();
  const { data: user, mutate } = useLoggedinUser();
  const { createNonce } = useE2EEContext();

  const onSubmit = async ({ password }) => {
    const userId = { email: user.email, name: `${user.first_name} ${user.last_name}` };
    const { privateKeyArmored: private_key_armored, publicKeyArmored: public_key_armored } = await generateKeys(userId, password);

    const updatedUser = await dispatch(passwordReset({ password: sha512(password), private_key_armored, public_key_armored })).unwrap();

    await createNonce(updatedUser, password);

    await mutate();

    if (onComplete) {
      if (updatedUser.public_key_armored) {
        updatedUser.publicKey = await openpgp.readKey({ armoredKey: expand(updatedUser.public_key_armored) });
      }
      onComplete(updatedUser);
    }
  }
  
  const toggleShowPassword = (): void => setState({
    ...state,
    showPassword: !state.showPassword,
  });

  return (
    <Formik
      onSubmit={onSubmit}
      validationSchema={validation}
      initialValues={{ password: '' }}
    >
      <Form style={{ width: '100%' }}>
        <Box marginBottom="20px">
          Before you can interact with an encrypted pack, you will need to create a password for your account. This will be used to encrypt/decrypt encrypted packs. It can also be used to log into Packs going forward.
        </Box>
        <FormAlert error={state.error} success={state.success} />
        <Field key="password" name="password">
          {({ field, meta, form }: FieldProps) => (
            <FormControl
              isInvalid={Boolean(meta.touched && form.errors[field.name])}
              isRequired={true}
            >
              <InputGroup size="md">
                <InputLeftElement height="100%" width="30px" ml="15px">
                  <PadlockIcon />
                </InputLeftElement>
                <Input
                  pr="2rem"
                  pl="4rem"
                  size="lg"
                  placeholder='Password'
                  isRequired={true}
                  type={state.showPassword ? "text" : "password"}
                  {...field}
                />
                <InputRightElement mr="15px" height="100%">
                  {state.showPassword ? (
                    <Tooltip label="Hide password">
                      <Button onClick={toggleShowPassword}>
                        <Icon boxSize="24px" as={AiOutlineEye} />
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip label="Show password">
                      <Button colorScheme="brand" onClick={toggleShowPassword}>
                        <Icon boxSize="24px" as={AiOutlineEyeInvisible} />
                      </Button>
                    </Tooltip>
                  )}
                </InputRightElement>
              </InputGroup>
            </FormControl>
          )}
        </Field>
        <Box marginTop="20px">
          <ButtonSubmit
            name="login"
            size="xl"
            isFullWidth={true}
          >
            Add account password
          </ButtonSubmit>
        </Box>
      </Form>
    </Formik>
  )
}